import { Link } from "react-router-dom";
import { Phone, Mail, Check, ArrowRight } from "lucide-react";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import { TEAM, FIRM } from "@/data/content";
import { FadeIn } from "@/components/Shared";

/**
 * Shared detail body for offence pages and childless group pages (Appeals, Youth Justice).
 * `item` needs: title, summary, covers[], faqs[]. `related`: [{label, href}]
 */
export default function ServiceDetailBody({ item, related = [] }) {
  const namita = TEAM[0];
  return (
    <section className="bg-white py-20 sm:py-28 lg:py-32">
      <div className="brand-container">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12">
          {/* Main */}
          <div className="lg:col-span-8">
            <FadeIn>
              <h2 className="brand-heading text-2xl sm:text-3xl">
                How we can help
              </h2>
              <p className="mt-5 leading-relaxed text-slate-600">{item.summary}</p>
            </FadeIn>

            {item.covers && (
              <FadeIn delay={0.1}>
                <div className="brand-panel-pale mt-10 border border-slate-200 p-8">
                  <p className="brand-eyebrow tracking-[0.22em]">
                    Our experience covers
                  </p>
                  <ul className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2">
                    {item.covers.map((c, i) => (
                      <li key={i} className="flex items-start gap-3 text-sm text-slate-700">
                        <Check className="mt-0.5 size-4 shrink-0 text-brand-gold" />
                        {c}
                      </li>
                    ))}
                  </ul>
                </div>
              </FadeIn>
            )}

            {item.faqs && (
              <FadeIn delay={0.15}>
                <div className="mt-12">
                  <h2 className="brand-heading text-2xl sm:text-3xl">
                    Frequently asked questions
                  </h2>
                  <Accordion type="single" collapsible className="mt-6" data-testid="service-faq-accordion">
                    {item.faqs.map((f, i) => (
                      <AccordionItem key={i} value={`faq-${i}`} data-testid={`faq-item-${i}`}>
                        <AccordionTrigger className="text-left text-base font-medium text-brand-navy hover:no-underline">
                          {f.q}
                        </AccordionTrigger>
                        <AccordionContent className="leading-relaxed text-slate-600">
                          {f.a}
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </div>
              </FadeIn>
            )}

            <FadeIn delay={0.2}>
              <div className="brand-panel-dark mt-12 p-8 sm:p-10">
                <h3 className="text-xl font-light tracking-tight">When to call us</h3>
                <p className="mt-3 text-sm leading-relaxed text-slate-300">
                  The earlier we are involved, the more we can do. Whether you have
                  been arrested, invited to an interview under caution, or simply
                  suspect you may be under investigation — speak to us before you
                  speak to anyone else. Our emergency line is answered 24 hours a day.
                </p>
                <a
                  href={`tel:${FIRM.emergencyPhone.replace(/\s/g, "")}`}
                  className="brand-cta-primary mt-6 inline-flex items-center gap-2 px-6 py-3 tracking-[0.2em]"
                  data-testid="service-emergency-call"
                >
                  <Phone className="size-4" /> 24hr: {FIRM.emergencyPhone}
                </a>
              </div>
            </FadeIn>
          </div>

          {/* Sidebar */}
          <aside className="lg:col-span-4">
            <FadeIn delay={0.1}>
              <div className="border border-slate-200 bg-white p-6" data-testid="key-contact-card">
                <p className="text-[10px] font-semibold uppercase tracking-[0.25em] text-slate-400">
                  Key contact
                </p>
                <img src={namita.image} alt={namita.name} className="mt-4 w-full object-cover" />
                <p className="mt-4 text-lg font-medium text-brand-navy">{namita.name}</p>
                <p className="text-xs uppercase tracking-[0.15em] text-slate-500">{namita.role}</p>
                <div className="mt-4 space-y-2 border-t border-slate-100 pt-4">
                  <a
                    href={`tel:${namita.phone.replace(/\s/g, "")}`}
                    className="flex items-center gap-2.5 text-sm text-slate-600 transition-colors hover:text-brand-terracotta"
                    data-testid="key-contact-phone"
                  >
                    <Phone className="size-4 text-brand-gold" /> {namita.phone}
                  </a>
                  <a
                    href={`mailto:${namita.email}`}
                    className="flex items-center gap-2.5 text-sm text-slate-600 transition-colors hover:text-brand-terracotta"
                    data-testid="key-contact-email"
                  >
                    <Mail className="size-4 text-brand-gold" /> {namita.email}
                  </a>
                </div>
                <Link
                  to={`/team/${namita.slug}`}
                  className="mt-5 block rounded-sm border border-brand-navy py-2.5 text-center text-sm font-semibold uppercase tracking-[0.2em] text-brand-navy transition-colors hover:bg-brand-navy hover:text-white"
                  data-testid="key-contact-profile-link"
                >
                  View profile
                </Link>
              </div>

              {related.length > 0 && (
                <div className="brand-panel-pale mt-6 border border-slate-200 p-6">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.25em] text-slate-400">
                    Related services
                  </p>
                  <ul className="mt-4 space-y-1">
                    {related.map((r) => (
                      <li key={r.href}>
                        <Link
                          to={r.href}
                          className="group flex items-center justify-between rounded px-2 py-2 text-sm text-slate-700 transition-colors hover:bg-white hover:text-brand-navy"
                          data-testid={`related-service-${r.href.split("/").pop()}`}
                        >
                          {r.label}
                          <ArrowRight className="size-3.5 text-brand-gold opacity-0 transition-opacity group-hover:opacity-100" />
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </FadeIn>
          </aside>
        </div>
      </div>
    </section>
  );
}
